import { supabase } from '../lib/supabase';

/**
 * ScheduleAdjusterService
 *
 * Applies recovery options to the sprint schedule:
 * - Skip tasks in sprints (compress)
 * - Skip entire sprints (skip optional)
 * - Add new sprints (extend day)
 * - Mark school sprints as skipped (shift to RD)
 * - Emergency triage (skip all but top schools)
 *
 * After each adjustment, recalculates sprint numbers and logs the change.
 */
class ScheduleAdjusterService {
  /**
   * Apply a recovery option to the schedule
   * @param {string} userId - Student ID
   * @param {string} optionId - Recovery option ID
   * @returns {Promise<Object>} Adjustment result
   */
  async applyRecoveryOption(userId, optionId) {
    try {
      // Get the recovery option and plan
      const { data: option, error: optionError } = await supabase
        .from('recovery_options')
        .select('*, recovery_plans!inner(user_id, schedule_id)')
        .eq('id', optionId)
        .single();

      if (optionError) throw optionError;

      // Verify user owns this recovery plan
      if (option.recovery_plans.user_id !== userId) {
        throw new Error('Unauthorized: User does not own this recovery plan');
      }

      const scheduleId = option.recovery_plans.schedule_id;

      // Apply the appropriate adjustment based on type
      let result;
      switch (option.type) {
        case 'COMPRESS_SPRINT':
          result = await this.compressSprint(scheduleId, option);
          break;
        case 'SKIP_OPTIONAL':
          result = await this.skipOptionalSprints(scheduleId, option);
          break;
        case 'EXTEND_DAY':
          result = await this.extendDay(scheduleId, option);
          break;
        case 'SHIFT_DEADLINE':
          result = await this.shiftDeadline(scheduleId, option);
          break;
        case 'EMERGENCY_TRIAGE':
          result = await this.emergencyTriage(scheduleId, option, userId);
          break;
        default:
          throw new Error(`Unknown recovery type: ${option.type}`);
      }

      // Mark recovery plan as applied
      await supabase
        .from('recovery_plans')
        .update({
          status: 'APPLIED',
          applied_at: new Date().toISOString()
        })
        .eq('id', option.recovery_plan_id);

      // Log the adjustment
      await this.logAdjustment({
        schedule_id: scheduleId,
        user_id: userId,
        adjustment_type: this.getAdjustmentType(option.type),
        reason: `Applied recovery option: ${option.title}`,
        changes: result,
        recovery_plan_id: option.recovery_plan_id,
        affected_sprint_ids: result.affected_sprint_ids || []
      });

      console.log('[ScheduleAdjusterService] Recovery option applied:', option.type);
      return result;
    } catch (error) {
      console.error('[ScheduleAdjusterService] Error applying recovery option:', error);
      throw error;
    }
  }

  /**
   * Compress sprint by skipping optional tasks
   * @param {string} scheduleId - Schedule ID
   * @param {Object} option - Recovery option
   * @returns {Promise<Object>} Adjustment result
   */
  async compressSprint(scheduleId, option) {
    // Get next incomplete sprint
    const { data: sprints, error } = await supabase
      .from('sprints')
      .select('*, sprint_tasks!inner(*, timeline_items!inner(*))')
      .eq('schedule_id', scheduleId)
      .in('status', ['NOT_STARTED', 'IN_PROGRESS'])
      .order('sprint_number', { ascending: true })
      .limit(1);

    if (error) throw error;
    if (!sprints || sprints.length === 0) {
      throw new Error('No incomplete sprints found');
    }

    const sprint = sprints[0];

    // Find optional tasks in this sprint
    const optionalTasks = sprint.sprint_tasks.filter(t => t.is_optional);

    if (optionalTasks.length === 0) {
      throw new Error('No optional tasks found in next sprint');
    }

    // Mark optional tasks as skipped
    const taskIds = optionalTasks.map(t => t.id);
    await supabase
      .from('sprint_tasks')
      .update({ status: 'SKIPPED' })
      .in('id', taskIds);

    // Update sprint estimated duration
    const timeSaved = optionalTasks.reduce((sum, t) => sum + (t.estimated_duration || 0), 0);
    await supabase
      .from('sprints')
      .update({
        estimated_duration: sprint.estimated_duration - timeSaved,
        updated_at: new Date().toISOString()
      })
      .eq('id', sprint.id);

    return {
      action: 'compress_sprint',
      sprint_id: sprint.id,
      sprint_name: sprint.name,
      tasks_skipped: taskIds.length,
      time_saved: timeSaved,
      affected_sprint_ids: [sprint.id]
    };
  }

  /**
   * Skip optional sprints entirely
   * @param {string} scheduleId - Schedule ID
   * @param {Object} option - Recovery option
   * @returns {Promise<Object>} Adjustment result
   */
  async skipOptionalSprints(scheduleId, option) {
    const sprintIds = option.changes.sprint_ids;

    if (!sprintIds || sprintIds.length === 0) {
      throw new Error('No sprint IDs provided');
    }

    // Mark sprints as skipped
    const { data: updatedSprints, error } = await supabase
      .from('sprints')
      .update({
        status: 'SKIPPED',
        updated_at: new Date().toISOString()
      })
      .in('id', sprintIds)
      .select();

    if (error) throw error;

    // Calculate time saved
    const timeSaved = updatedSprints.reduce((sum, s) => sum + s.estimated_duration, 0);

    return {
      action: 'skip_optional',
      sprints_skipped: updatedSprints.length,
      sprint_names: updatedSprints.map(s => s.name),
      time_saved: timeSaved,
      affected_sprint_ids: sprintIds
    };
  }

  /**
   * Add an evening sprint to catch up
   * @param {string} scheduleId - Schedule ID
   * @param {Object} option - Recovery option
   * @returns {Promise<Object>} Adjustment result
   */
  async extendDay(scheduleId, option) {
    // Get schedule and current day
    const { data: schedule, error: scheduleError } = await supabase
      .from('sprint_schedules')
      .select('*, sprints(*)')
      .eq('id', scheduleId)
      .single();

    if (scheduleError) throw scheduleError;

    const currentDay = schedule.current_day || 1;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get next incomplete tasks to include in evening sprint
    const { data: incompleteTasks, error: tasksError } = await supabase
      .from('timeline_items')
      .select('*')
      .eq('user_id', schedule.user_id)
      .not('completion_status', 'eq', 'completed')
      .limit(3);

    if (tasksError) throw tasksError;

    // Find highest sprint number for today
    const todaysSprints = schedule.sprints.filter(s => s.day_number === currentDay);
    const maxSprintNumber = Math.max(...todaysSprints.map(s => s.sprint_number), 0);

    // Create new evening sprint
    const { data: newSprint, error: insertError } = await supabase
      .from('sprints')
      .insert({
        schedule_id: scheduleId,
        user_id: schedule.user_id,
        name: 'Evening Catch-Up Sprint',
        description: 'Added to catch up on schedule',
        sprint_number: maxSprintNumber + 1,
        day_number: currentDay,
        date: today.toISOString().split('T')[0],
        type: 'MIXED',
        estimated_duration: option.changes.estimated_duration || 90,
        suggested_start_time: option.changes.suggested_time || '19:00',
        priority: 'HIGH',
        status: 'NOT_STARTED'
      })
      .select()
      .single();

    if (insertError) throw insertError;

    // Add tasks to the new sprint
    for (let i = 0; i < Math.min(incompleteTasks.length, 3); i++) {
      await supabase
        .from('sprint_tasks')
        .insert({
          sprint_id: newSprint.id,
          task_id: incompleteTasks[i].id,
          order_index: i + 1,
          estimated_duration: incompleteTasks[i].estimated_duration || 30,
          status: 'NOT_STARTED'
        });
    }

    return {
      action: 'add_evening_sprint',
      sprint_id: newSprint.id,
      sprint_name: newSprint.name,
      tasks_added: incompleteTasks.length,
      estimated_duration: newSprint.estimated_duration,
      affected_sprint_ids: [newSprint.id]
    };
  }

  /**
   * Shift school applications to RD
   * @param {string} scheduleId - Schedule ID
   * @param {Object} option - Recovery option
   * @returns {Promise<Object>} Adjustment result
   */
  async shiftDeadline(scheduleId, option) {
    // This would typically show a modal to let user select which schools
    // For now, we'll mark sprints as "can be skipped" for manual review

    const schoolIds = option.changes.school_options;

    // Get sprints for these schools
    const { data: sprints, error } = await supabase
      .from('sprints')
      .select('*')
      .eq('schedule_id', scheduleId)
      .in('school_id', schoolIds)
      .in('status', ['NOT_STARTED', 'IN_PROGRESS']);

    if (error) throw error;

    // Mark as optional for user to decide
    const sprintIds = sprints.map(s => s.id);
    await supabase
      .from('sprints')
      .update({
        is_optional: true,
        can_be_skipped: true,
        updated_at: new Date().toISOString()
      })
      .in('id', sprintIds);

    return {
      action: 'shift_deadline',
      sprints_marked_optional: sprints.length,
      schools_affected: schoolIds.length,
      message: 'Schools marked for potential RD shift. Review and decide which to defer.',
      affected_sprint_ids: sprintIds
    };
  }

  /**
   * Emergency triage - keep only top schools
   * @param {string} scheduleId - Schedule ID
   * @param {Object} option - Recovery option
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Adjustment result
   */
  async emergencyTriage(scheduleId, option, userId) {
    // Get all school sprints
    const { data: sprints, error } = await supabase
      .from('sprints')
      .select('*, college_journey_colleges!inner(name, priority)')
      .eq('schedule_id', scheduleId)
      .not('school_id', 'is', null)
      .in('status', ['NOT_STARTED', 'IN_PROGRESS']);

    if (error) throw error;

    // Group by school and sort by priority
    const schoolGroups = sprints.reduce((acc, sprint) => {
      const schoolId = sprint.school_id;
      if (!acc[schoolId]) {
        acc[schoolId] = {
          school_id: schoolId,
          school_name: sprint.college_journey_colleges?.name || 'Unknown',
          priority: sprint.college_journey_colleges?.priority || 999,
          sprints: []
        };
      }
      acc[schoolId].sprints.push(sprint);
      return acc;
    }, {});

    // Sort schools by priority (lower number = higher priority)
    const sortedSchools = Object.values(schoolGroups).sort((a, b) => a.priority - b.priority);

    // Keep top 2 schools, skip the rest
    const schoolsToKeep = sortedSchools.slice(0, 2);
    const schoolsToSkip = sortedSchools.slice(2);

    const sprintsToSkip = schoolsToSkip.flatMap(school => school.sprints.map(s => s.id));

    if (sprintsToSkip.length > 0) {
      await supabase
        .from('sprints')
        .update({
          status: 'SKIPPED',
          updated_at: new Date().toISOString()
        })
        .in('id', sprintsToSkip);
    }

    return {
      action: 'emergency_triage',
      schools_kept: schoolsToKeep.length,
      schools_kept_names: schoolsToKeep.map(s => s.school_name),
      schools_deferred: schoolsToSkip.length,
      schools_deferred_names: schoolsToSkip.map(s => s.school_name),
      sprints_skipped: sprintsToSkip.length,
      affected_sprint_ids: sprintsToSkip
    };
  }

  /**
   * Log an adjustment to the database
   * @param {Object} adjustment - Adjustment data
   * @returns {Promise<Object>} Logged adjustment
   */
  async logAdjustment(adjustment) {
    try {
      const { data, error } = await supabase
        .from('sprint_adjustments')
        .insert({
          ...adjustment,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[ScheduleAdjusterService] Adjustment logged:', data.id);
      return data;
    } catch (error) {
      console.error('[ScheduleAdjusterService] Error logging adjustment:', error);
      throw error;
    }
  }

  /**
   * Get adjustment history for a schedule
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Array>} Array of adjustments
   */
  async getAdjustmentHistory(scheduleId) {
    try {
      const { data, error } = await supabase
        .from('sprint_adjustments')
        .select('*')
        .eq('schedule_id', scheduleId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[ScheduleAdjusterService] Error getting adjustment history:', error);
      throw error;
    }
  }

  /**
   * Map recovery type to adjustment type
   * @param {string} recoveryType - Recovery type
   * @returns {string} Adjustment type
   */
  getAdjustmentType(recoveryType) {
    const mapping = {
      COMPRESS_SPRINT: 'SPRINT_COMPRESSED',
      SKIP_OPTIONAL: 'SPRINT_SKIPPED',
      EXTEND_DAY: 'SPRINT_ADDED',
      SHIFT_DEADLINE: 'DEADLINE_SHIFTED',
      EMERGENCY_TRIAGE: 'SCHEDULE_REBUILT'
    };
    return mapping[recoveryType] || 'SPRINT_COMPRESSED';
  }

  /**
   * Undo a recovery plan (restore skipped sprints)
   * @param {string} userId - Student ID
   * @param {string} recoveryPlanId - Recovery plan ID to undo
   * @returns {Promise<Object>} Undo result
   */
  async undoRecoveryPlan(userId, recoveryPlanId) {
    try {
      console.log('[ScheduleAdjusterService] Undoing recovery plan:', recoveryPlanId);

      // Get the recovery plan
      const { data: plan, error: planError } = await supabase
        .from('recovery_plans')
        .select('*, recovery_options!inner(id)')
        .eq('id', recoveryPlanId)
        .eq('user_id', userId)
        .single();

      if (planError) throw planError;

      if (plan.status !== 'APPLIED') {
        throw new Error('Cannot undo a recovery plan that was not applied');
      }

      // Get the adjustment log to see what was changed
      const { data: adjustment, error: adjustmentError } = await supabase
        .from('sprint_adjustments')
        .select('*')
        .eq('recovery_plan_id', recoveryPlanId)
        .single();

      if (adjustmentError) throw adjustmentError;

      const affectedSprintIds = adjustment.affected_sprint_ids || [];

      if (affectedSprintIds.length === 0) {
        throw new Error('No sprints were affected by this recovery plan');
      }

      // Restore the affected sprints to NOT_STARTED status
      const { data: restoredSprints, error: restoreError } = await supabase
        .from('sprints')
        .update({
          status: 'NOT_STARTED',
          is_optional: false,
          can_be_skipped: false,
          updated_at: new Date().toISOString()
        })
        .in('id', affectedSprintIds)
        .select();

      if (restoreError) throw restoreError;

      // Mark recovery plan as undone
      await supabase
        .from('recovery_plans')
        .update({
          status: 'UNDONE',
          updated_at: new Date().toISOString()
        })
        .eq('id', recoveryPlanId);

      // Log the undo action
      await this.logAdjustment({
        schedule_id: adjustment.schedule_id,
        user_id: userId,
        adjustment_type: 'SCHEDULE_REBUILT',
        reason: `Undid recovery plan: ${recoveryPlanId}`,
        changes: {
          action: 'undo_recovery',
          sprints_restored: restoredSprints.length,
          sprint_names: restoredSprints.map(s => s.name)
        },
        recovery_plan_id: recoveryPlanId,
        affected_sprint_ids: affectedSprintIds
      });

      console.log('[ScheduleAdjusterService] Recovery plan undone. Restored', restoredSprints.length, 'sprints');

      return {
        action: 'undo_recovery',
        sprints_restored: restoredSprints.length,
        sprint_names: restoredSprints.map(s => s.name),
        affected_sprint_ids: affectedSprintIds
      };
    } catch (error) {
      console.error('[ScheduleAdjusterService] Error undoing recovery plan:', error);
      throw error;
    }
  }

  /**
   * Recalculate sprint schedule after adjustments
   * This would renumber sprints and update dependencies
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Recalculation result
   */
  async recalculateSchedule(scheduleId) {
    try {
      // Get all active sprints
      const { data: sprints, error } = await supabase
        .from('sprints')
        .select('*')
        .eq('schedule_id', scheduleId)
        .neq('status', 'SKIPPED')
        .order('day_number', { ascending: true })
        .order('sprint_number', { ascending: true });

      if (error) throw error;

      // Renumber sprints
      let globalSprintNumber = 1;
      for (const sprint of sprints) {
        await supabase
          .from('sprints')
          .update({
            sprint_number: globalSprintNumber,
            updated_at: new Date().toISOString()
          })
          .eq('id', sprint.id);

        globalSprintNumber++;
      }

      // Update schedule overall progress
      const completedSprints = sprints.filter(s => s.status === 'COMPLETE').length;
      const totalSprints = sprints.length;
      const overallProgress = totalSprints > 0
        ? Math.floor((completedSprints / totalSprints) * 100)
        : 0;

      await supabase
        .from('sprint_schedules')
        .update({
          overall_progress: overallProgress,
          updated_at: new Date().toISOString()
        })
        .eq('id', scheduleId);

      console.log('[ScheduleAdjusterService] Schedule recalculated');
      return {
        total_sprints: totalSprints,
        completed_sprints: completedSprints,
        overall_progress: overallProgress
      };
    } catch (error) {
      console.error('[ScheduleAdjusterService] Error recalculating schedule:', error);
      throw error;
    }
  }
}

export default new ScheduleAdjusterService();
